// API utility functions for consistent error handling and response processing

import { AxiosError } from 'axios';
import { ApiErrorResponse } from '@/types';

/**
 * Type guard to check if an error is an API error with proper typing
 */
export function isApiError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    error instanceof Error &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'error' in error.response.data &&
    'status' in error.response.data
  );
}

/**
 * Extract error message from API error with fallback
 */
export function getApiErrorMessage(error: unknown): string {
  if (isApiError(error) && error.response?.data) {
    return error.response.data.error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Extract status code from API error with fallback
 */
export function getApiErrorStatus(error: unknown): number {
  if (isApiError(error) && error.response?.data) {
    return error.response.data.status;
  }

  if (error instanceof Error && 'response' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status || 500;
  }

  return 500;
}

/**
 * Create a standardized error object for consistent handling
 */
export function createStandardError(error: unknown): {
  message: string;
  status: number;
  isApiError: boolean;
} {
  return {
    message: getApiErrorMessage(error),
    status: getApiErrorStatus(error),
    isApiError: isApiError(error),
  };
}

/**
 * Log API errors in a consistent format
 */
export function logApiError(error: unknown, context?: string): void {
  const standardError = createStandardError(error);
  const prefix = context ? `[${context}]` : '[API Error]';

  console.error(
    `${prefix} ${standardError.status}: ${standardError.message}`,
    standardError.isApiError ? error : { error, standardError }
  );
}
